import { create } from "zustand";
import { useProjectStore } from "@/zapi-frontend/stores/project";

export type TVersionsInlineMode = "new" | "rename" | "delete";

export type TVersionsInline = {
  rowId: string;
  mode: TVersionsInlineMode;
  value: string;
  sourceId: string | null;
  targetId: string | null;
  pending: boolean;
};

type TStore = {
  inline: TVersionsInline | null;
  setInline: (next: TVersionsInline | null) => void;
  patchInline: (patch: Partial<TVersionsInline>) => void;
  optimisticRename: (rowProjectId: string, versionId: string, newName: string) => void;
  optimisticAdd: (rowProjectId: string, name: string) => void;
  optimisticRemove: (rowProjectId: string, versionId: string) => void;
};

export const useProjectVersionsStore = create<TStore>((set) => ({
  inline: null,
  setInline: (next) => set({ inline: next }),
  patchInline: (patch) => set((s) => ({ inline: s.inline ? { ...s.inline, ...patch } : null })),

  optimisticRename: (rowProjectId, versionId, newName) => {
    const { projects, setProjects } = useProjectStore.getState();
    setProjects(
      projects.map((p) =>
        p.id === rowProjectId
          ? { ...p, versions: p.versions?.map((v) => (v.id === versionId ? { ...v, name: newName } : v)) }
          : p
      )
    );
  },

  optimisticAdd: (rowProjectId, name) => {
    const { projects, setProjects } = useProjectStore.getState();
    const now = new Date().toISOString();
    setProjects(
      projects.map((p) =>
        p.id === rowProjectId
          ? { ...p, versions: [{ id: `optimistic-${Date.now()}`, name, date: now }, ...(p.versions ?? [])] }
          : p
      )
    );
  },

  optimisticRemove: (rowProjectId, versionId) => {
    const { projects, setProjects } = useProjectStore.getState();
    setProjects(
      projects.map((p) => {
        if (p.id !== rowProjectId) return p;
        const remaining = (p.versions ?? []).filter((v) => v.id !== versionId);
        if (remaining.length === 0) return { ...p, versionGroupId: null, versions: [] };
        return { ...p, versions: remaining };
      })
    );
  }
}));
